<template>
  <div class="weather-details" data-test-id="weather-details">
    <div class="weather-details__top">
      <div
        class="weather-details__temperature"
        :aria-label="accessibleTemperature"
        data-test-id="weather-details-temperature"
      >
        {{ temperature }}
      </div>
      <form
        class="weather-details__units"
        :aria-label="$t('web.widgets.weather.switchunits')"
      >
        <label
          :class="{ 'weather-details__unit--active': units === 'metric' }"
          class="weather-details__unit"
          data-test-id="weather-details-unit-label-metric"
        >
          °C
          <input
            class="weather-details__unit-input"
            type="radio"
            name="unit"
            value="metric"
            data-test-id="weather-details-unit-metric"
            :checked="units === 'metric'"
            @change="onUnitChange"
          >
        </label>
        <span class="weather-details__separator" aria-hidden="true">|</span>
        <label
          :class="{ 'weather-details__unit--active': units === 'imperial' }"
          class="weather-details__unit"
          data-test-id="weather-details-unit-label-imperial"
        >
          °F
          <input
            class="weather-details__unit-input"
            type="radio"
            name="unit"
            value="imperial"
            data-test-id="weather-details-unit-imperial"
            :checked="units === 'imperial'"
            @change="onUnitChange"
          >
        </label>
      </form>
      <EConditionIcon
        :code="code"
        aria-hidden="true"
        class="weather-details__icon"
        type="color"
      />
    </div>
    <div class="weather-details__content">
      <div class="weather-details__description">
        {{ description }}
      </div>
      <div class="weather-details__location">
        {{ location }}
      </div>
      <time
        :datetime="isoDateTime"
        class="weather-details__time"
        data-test-id="weather-details-time"
      >
        {{ formattedDate }}
      </time>
      <div class="weather-details__secondary">
        <span class="weather-details__secondary-item">
          {{ $t('web.widgets.weather.precipitation') }}: {{ rainAmount }}%
          <span class="weather-details__separator">|</span>
        </span>
        <span class="weather-details__secondary-item" data-test-id="weather-details-wind">
          {{ $t('web.widgets.weather.wind') }}: {{ formattedWind }}
          <span class="weather-details__separator">|</span>
        </span>
        <span class="weather-details__secondary-item">
          {{ $t('web.widgets.weather.humidity') }}: {{ humidity }}%
        </span>
      </div>
      <div class="weather-details__sun-times">
        <span class="sr-only">{{ $t('web.widgets.weather.sunrise') }}:</span>
        <time>{{ sunriseTime }}</time>
        <EIcon
          id="weather-outline-sunrise-sunset"
          class="weather-details__sun-times-icon"
          aria-hidden="true"
        />
        <span class="sr-only">{{ $t('web.widgets.weather.sunset') }}:</span>
        <time>{{ sunsetTime }}</time>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import upperFirst from 'lodash/upperFirst';

import { isValidDate, isUnitSystem } from '~~/common/js/universal/validators';
import EConditionIcon from '~/components/weather/icons/condition-icon';
import EIcon from '~/components/weather/icons/icon';

const timeFormat = { hour: '2-digit', minute: '2-digit' };

export default {
  name: 'EDetails',
  components: {
    EConditionIcon,
    EIcon,
  },
  props: {
    description: {
      type: String,
      required: true,
    },
    code: {
      type: String,
      required: true,
    },
    humidity: {
      type: Number,
      required: true,
    },
    location: {
      type: String,
      required: true,
    },
    rainAmount: {
      type: Number,
      required: true,
    },
    selected: {
      type: Number,
      required: true,
    },
    sunrise: {
      type: Date,
      required: true,
    },
    sunset: {
      type: Date,
      required: true,
    },
    units: {
      type: String,
      required: true,
      validator: isUnitSystem,
    },
    temperature: {
      type: Number,
      required: true,
    },
    time: {
      type: Date,
      required: true,
      validator: isValidDate,
    },
    windSpeed: {
      type: Number,
      required: true,
    },
  },
  computed: {
    ...mapState('global', ['locale']),
    formattedWind() {
      const unit = this.units === 'metric' ? 'km/h' : 'mph';
      return `${this.windSpeed} ${unit}`;
    },
    formattedDate() {
      const dayOfWeek = this.time.toLocaleDateString(this.locale, { weekday: 'long' });
      const parts = [upperFirst(dayOfWeek)];
      if (this.selected === 0) {
        parts.push(this.time.toLocaleTimeString(this.locale, timeFormat));
      }
      return parts.join(', ');
    },
    isoDateTime() {
      return this.time.toISOString();
    },
    sunriseTime() {
      return this.sunrise.toLocaleTimeString(this.locale, timeFormat);
    },
    sunsetTime() {
      return this.sunset.toLocaleTimeString(this.locale, timeFormat);
    },
    accessibleTemperature() {
      const unit = this.units === 'metric' ? '°C' : '°F';
      return `${this.temperature}${unit}`;
    },
  },
  methods: {
    onUnitChange({ target }) {
      if (target.checked) {
        this.$emit('setUnits', target.value);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$icon-size-mobile: 90px;
$icon-size-desktop: 145px;
$temperature-unit-label: 28px;

.weather-details {
  position: relative;
}

.weather-details__top {
  display: flex;
  padding-top: $space-m;

  @include desktop {
    padding-top: $space-1s;
  }
}

.weather-details__temperature {
  font-size: $font-5l;
  line-height: 1;
  font-weight: $font-weight-1l;
}

.weather-details__units {
  color: var(--color-text-secondary);
  margin-left: $space-s;
  margin-right: $space-s;
}

.weather-details__unit {
  display: inline-block;
  text-align: center;
  width: $temperature-unit-label;
  cursor: pointer;
  padding: 0 $space-2s $space-2s;
  transition: color $timing-2s $easing;
  border-bottom: $space-2s solid transparent;

  &:hover,
  &:focus,
  &:focus-within {
    @extend %keyboard-nav-focus;

    color: var(--color-text-primary);
    border-bottom-color: var(--color-text-primary);
  }
}

.weather-details__unit.weather-details__unit--active {
  border-bottom-color: var(--color-brand-primary);
  font-weight: $font-weight-1l;
  color: var(--color-brand-primary);
}

.weather-details__unit-input {
  @extend %sr-only;
}

.weather-details__separator {
  color: var(--color-decorative-border-1);
}

.weather-details__icon {
  margin-top: -$space-1l;
  width: $icon-size-mobile;
  height: $icon-size-mobile;

  @include desktop {
    position: absolute;
    margin-top: 0;
    right: $space-4l;
    left: auto;
    top: $space-2l;
    width: $icon-size-desktop;
    height: $icon-size-desktop;
  }
}

.weather-details__content {
  margin-right: $space-l;
}

.weather-details__description {
  font-size: $font-l;
  font-weight: $font-weight-1l;

  @include desktop {
    margin: $space-s 0 0;
    max-width: 70%;
  }

  @include desktop-l {
    max-width: 65%;
  }
}

.weather-details__location,
.weather-details__time {
  line-height: $line-height-m;
}

.weather-details__secondary,
.weather-details__sun-times {
  color: var(--color-text-secondary);
}

.weather-details__sun-times-icon {
  margin-left: $space-2s;
  margin-right: $space-2s;
  // TODO: non-DS sizes, check why
  width: 17px;
  height: 11px;
  vertical-align: baseline;
}
</style>
